'use client';

import { AttributeModal } from '@/components/dialogs/attribute-modal/attribute-modal';
import { IcQuestionsIcon } from '@/components/icons/ic-questions-icon';
import { Button } from '@/components/ui/button';
import { WatchListIconComponent } from '@/components/watch-list-icon/watch-list-icon';
import { useTheaterContext } from '@/hooks/state/theater';
import { dataTestTransformer } from '@/utils/data-test-helper';
import { formatDateWithTimezoneLong, formatDateWithTimezoneShort } from '@/utils/date-time-helper';
import { getRuntimeString } from '@/utils/movie-helper';
import { Attribute, Movie } from '@cinemark/content-api-client';
import { useEffect, useRef, useState } from 'react';

type MovieDetailInfoProps = {
  movie: Movie;
};

type MovieInfoData = {
  header: string;
  text?: string;
  textShort?: string;
};

export const MovieDetailInfo = ({ movie }: Readonly<MovieDetailInfoProps>): React.JSX.Element => {
  const { data: theater, isLoading: isLoadingTheater } = useTheaterContext();
  const [movieInfo, setMovieInfo] = useState<MovieInfoData[]>([]);
  const [hasSynopsisOverflow, setHasSynopsisOverflow] = useState(false);
  const [isSynopsisExpanded, setIsSynopsisExpanded] = useState(false);
  const synopsisContainerRef = useRef<HTMLDivElement>(null);
  const clampedSynopsisRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (isLoadingTheater || !movie) return;

    setMovieInfo([
      {
        header: 'Rating',
        text: movie.rating,
      },
      {
        header: 'Runtime',
        text: getRuntimeString(movie.runningTime),
      },
      {
        header: 'Release Date',
        text: formatDateWithTimezoneLong(movie.releaseDate, theater?.timeZone),
        textShort: formatDateWithTimezoneShort(movie.releaseDate, theater?.timeZone),
      },
      {
        header: 'Genre',
        text: movie.genre,
      },
    ]);
  }, [theater, isLoadingTheater, movie]);

  useEffect(() => {
    const synopsisContainer = synopsisContainerRef.current;
    const clampedSynopsis = clampedSynopsisRef.current;
    if (!synopsisContainer || !clampedSynopsis) return;

    const updateSynopsisOverflow = (): void => {
      setHasSynopsisOverflow(clampedSynopsis.scrollHeight > clampedSynopsis.clientHeight);
    };

    updateSynopsisOverflow();

    const resizeObserver = new ResizeObserver(updateSynopsisOverflow);
    resizeObserver.observe(synopsisContainer);
    window.addEventListener('resize', updateSynopsisOverflow);

    return (): void => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateSynopsisOverflow);
    };
  }, [movie.synopsis]);

  const displayAttributeItem = (attribute: Attribute, index: number): React.JSX.Element => {
    const attributeText = <p key={attribute.id}
      data-test={`movie-attribute-${dataTestTransformer(attribute.displayName || `${index}`)}`}
    >
      {attribute.displayName}
    </p>;

    if (!attribute.contentPage) return attributeText;

    return (
      <div key={attribute.id}
        className="flex items-center gap-xs"
      >
        <p data-test={`movie-attribute-${dataTestTransformer(attribute.displayName || `${index}`)}`}>{attribute.displayName}</p>
        <AttributeModal key={`${attribute.displayName}-${index}`}
          attribute={attribute}
        >
          <Button className="p-0 h-auto hover:bg-transparent"
            data-test={`button-attribute-${dataTestTransformer(attribute.displayName ?? attribute.imageAltText ?? 'attribute')}`}
            variant="ghost"
          >
            <IcQuestionsIcon className="size-4" />
          </Button>
        </AttributeModal>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-ms md:gap-md max-md:shadow-md max-md:pb-ms max-md:rounded-s-md">
      <div className="flex items-center justify-between">
        <h1 className="max-sm:text-lg"
          data-test="movie-title"
        >
          {movie.movieTitle}
        </h1>
        <WatchListIconComponent showAddToWatchListText
          className="size-4 text-primary"
          movieId={movie.cinemarkMovieId || 0}
          movieTitle={movie.movieTitle || ''}
        />
      </div>

      {!!movie.movieTitleAttributes?.length &&
        <div className="max-sm:hidden flex flex-wrap gap-xs">
          {movie.movieTitleAttributes.map(displayAttributeItem)}
        </div>
      }

      {!!movieInfo.length &&
        <div className="flex gap-x-md gap-y-xs flex-wrap">
          {movieInfo.map(info =>
            <div key={info.header}
              className="flex flex-col"
            >
              <small className="hidden md:flex"
                data-test={`movie-info-header-${dataTestTransformer(info.header)}`}
              >
                {info.header}
              </small>
              <p className="max-sm:font-semibold"
                data-test={`movie-info-text-${dataTestTransformer(info.header)}`}
              >
                {info.textShort &&
                  <>
                    <span className="hidden md:inline">{info.text}</span>
                    <span className="md:hidden">{info.textShort}</span>
                  </>
                }
                {!info.textShort && info.text}
              </p>
            </div>
          )}
        </div>
      }

      {movie.synopsis &&
        <div ref={synopsisContainerRef}
          className="relative flex flex-col gap-xs"
        >
          <p ref={clampedSynopsisRef}
            aria-hidden="true"
            className="invisible absolute top-0 left-0 w-full line-clamp-3"
            data-test="movie-synopsis-measurement"
          >
            {movie.synopsis}
          </p>
          <p className={isSynopsisExpanded ? undefined : 'line-clamp-3'}
            data-test="movie-synopsis"
          >
            {movie.synopsis}
          </p>
          {hasSynopsisOverflow &&
            <Button className="self-end"
              data-test="button-synopsis-toggle"
              type="button"
              variant="link"
              onClick={() => setIsSynopsisExpanded(prev => !prev)}
            >
              {isSynopsisExpanded ? 'Show Less' : 'Read More'}
            </Button>
          }
        </div>
      }
    </div>
  );
};
